import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  ClaimDetailItem,
  applyClaimDetailVo,
  getClaimStatusColor,
  getClaimStatusLabel,
} from '../claim-detail/claim-detail-view.util';

interface PdfBasicFormRawValue {
  referenceType?: string;
  scheme?: string | null;
  selfReturn?: string;
}

@Component({
  selector: 'claim-detail-pdf',
  templateUrl: './claim-detail-pdf.component.html',
  styleUrls: ['./claim-detail-pdf.component.less'],
})
export class ClaimDetailPdfComponent implements OnInit {
  claimNumber = '';
  claimStatus = '';
  claimStatusName = '';

  basicForm!: FormGroup;
  otherForm!: FormGroup;
  claimReferences: any[] = [];

  referenceTypeOptions = [
    { labelKey: 'CLAIM_STANDARD_BINDING_ORDER', value: 'normal' },
    { labelKey: 'CLAIM_CONSIGNMENT_ORDER', value: 'consignment' },
  ];

  get selectedReferenceTypeOptions(): { labelKey: string; value: string }[] {
    const selected = this.getFormRawValue().referenceType;
    if (!selected) {
      return [];
    }
    return this.referenceTypeOptions.filter(opt => opt.value === selected);
  }

  schemeOptions: { label: string; value: string }[] = [];

  selfReturnOptions = [
    { labelKey: 'CLAIM_CREATE_RETURN_BY_SELF', value: 'yes' },
    { labelKey: 'CLAIM_CREATE_BOSCH_PICKUP', value: 'no' },
  ];

  warehouseInfo = {
    address: '江苏省苏州市太仓市港口开发区金港路2号博世亚太物流中心A1仓库',
    contact: '侯昊松',
    phone: '13526207938',
  };

  detailItems: ClaimDetailItem[] = [];

  claimTypeName = '';
  claimCategoryName = '';
  ruleLevel2Name = '';
  contactAddress = '';
  contactName = '';
  contactPhone = '';
  returnSo = '';
  returnDn = '';
  trackingNum = '';

  /** 后端 selfReturn 原始值，用于 PDF 展示退货方式区块 */
  private voSelfReturn: number | null = null;

  private static readonly RETURN_SCHEMES = ['Return for Credit', 'Exchange', 'Return'];

  detailLoading = false;
  itemsLoading = false;

  detailPagination = {
    pageIndex: 1,
    pageSize: 5,
  };

  get pagedDetailItems(): ClaimDetailItem[] {
    const start = (this.detailPagination.pageIndex - 1) * this.detailPagination.pageSize;
    return this.detailItems.slice(start, start + this.detailPagination.pageSize);
  }

  attachments: { attachmentId: string; name: string }[] = [];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // 动态创建时 export 会先 applyClaimDetail 再 detectChanges；避免重复 init 覆盖已回填数据。
    if (!this.basicForm) {
      this.initForms();
    }
  }

  private initForms(): void {
    this.basicForm = this.fb.group({
      referenceType: ['normal'],
      reason: [null],
      category: [null],
      indexNumber: [null],
      scheme: [null],
      selfReturn: ['no'],
      customerAddress: [''],
      customerContact: [''],
      customerPhone: [''],
    });

    this.otherForm = this.fb.group({
      remark: [''],
    });
  }

  applyClaimDetail(vo: any): void {
    if (!this.basicForm) {
      this.initForms();
    }

    applyClaimDetailVo(this, vo);
    this.voSelfReturn = vo?.selfReturn ?? null;

    const scheme = vo?.ruleLevel2;
    if (scheme != null && this.ruleLevel2Name) {
      const opt = this.schemeOptions.find(o => o.value === scheme);
      if (opt) {
        opt.label = this.ruleLevel2Name;
      }
    }

    // PDF 导出展示全部明细，分页仅用于保持与详情页一致的样式结构
    this.detailPagination = {
      pageIndex: 1,
      pageSize: Math.max(this.detailItems.length, 5),
    };
    // 不禁用表单：disable 后 control.value 为 null，会导致退货方式等 *ngIf 判断失败
  }

  getStatusLabel(status: string): string {
    return getClaimStatusLabel(status);
  }

  getStatusColor(status: string): { color: string; bg: string } {
    return getClaimStatusColor(status);
  }

  get isPriceDifferenceMode(): boolean {
    if (this.getFormRawValue().referenceType !== 'normal') return false;
    return this.claimTypeName === '价格差异' && this.claimCategoryName === '价格差异';
  }

  get showSelfReturnSection(): boolean {
    const allowed = ClaimDetailPdfComponent.RETURN_SCHEMES;
    const name = this.ruleLevel2Name?.trim() ?? '';
    if (name && allowed.includes(name)) {
      return true;
    }

    const schemeValue = this.getFormRawValue().scheme;
    if (schemeValue != null && schemeValue !== '') {
      if (allowed.includes(String(schemeValue).trim())) {
        return true;
      }
      const matched = this.schemeOptions.find(opt => opt.value === schemeValue);
      const label = (matched?.label ?? '').toString().trim();
      if (allowed.includes(label)) {
        return true;
      }
    }

    if (this.voSelfReturn === 1) {
      return true;
    }

    return !!(
      this.returnSo ||
      this.returnDn ||
      this.trackingNum ||
      this.contactAddress ||
      this.contactName ||
      this.contactPhone
    );
  }

  get isSelfReturnYes(): boolean {
    return this.getFormRawValue().selfReturn === 'yes';
  }

  get isSelfReturnNo(): boolean {
    return this.getFormRawValue().selfReturn === 'no';
  }

  private getFormRawValue(): PdfBasicFormRawValue {
    return (this.basicForm?.getRawValue?.() ?? {}) as PdfBasicFormRawValue;
  }

  get warehouseInfoText(): string {
    return `仓库地址：${this.warehouseInfo.address}； 仓库联系人：${this.warehouseInfo.contact}； 仓库电话：${this.warehouseInfo.phone}`;
  }

  get isOrderMode(): boolean {
    return this.getFormRawValue().referenceType === 'consignment';
  }
}
